import { DetailedInsight, ExploreSingleInsight } from '@api';
import { Badge } from '@design';
import { getConditionIcons } from '@terminal/components/insight-icons/utils';
import { InsightIconsRoot } from '@terminal/components/insight-icons-root/InsightIconsRoot';
import { IconConfig } from '@terminal/components/insights-legacy-drawer/ScenarioInsightsLegacyDrawer';
import { formatUtcDate } from '@terminal/services/insights/utils/insight-utils';
import { getExploreDirection } from '@terminal/views/dashboard/whats-new-widget/components/insight-card/utils';
import clsx from 'clsx';
import { isSameDay } from 'date-fns';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { InsightDetails } from '../insight-card/insights-details/InsightDetails';
import styles from './ExploreInsightCard.module.scss';

const SINGLE_ICONS_SIZE = { width: '34px', height: '40px' };
const COMBINE_ICONS_SIZE = { width: '34px', height: '72px' };

export interface ExploreInsightCardProps {
  insight: DetailedInsight;
  exploreInsight: ExploreSingleInsight;
  activeInsightId: string;
  dataInsightId: string;
  ref: (el: HTMLDivElement | null) => void;
  handleSetActiveInsightId: (insightId: string) => void;
  handleSetIconConfig?: (config: IconConfig) => void;
}

export const ExploreInsightCard = ({
  insight,
  exploreInsight,
  activeInsightId,
  handleSetActiveInsightId,
  handleSetIconConfig,
  dataInsightId,
  ref,
}: ExploreInsightCardProps) => {
  const { t } = useTranslation(['insights']);
  const {
    metadata,
    created_at: createAt,
    card,
    episodes,
    simple_title,
  } = insight;

  const isoDate = formatUtcDate(createAt, 'd MMM, yyyy');
  const isNewInsight = isSameDay(new Date(createAt), new Date());
  const episodeLabel = t('insights:insightCard.episode', {
    count: episodes?.length ?? 0,
  });

  const conditions = getConditionIcons(exploreInsight);
  const isCombined = conditions.length > 1;
  const entityTag = insight.metadata.entity;
  const formattedDirection = getExploreDirection(insight.metadata.direction);

  const handleClick = () => {
    handleSetIconConfig?.({
      conditions,
      direction: exploreInsight.direction,
      isCombined,
      layout: 'horizontal',
    });

    handleSetActiveInsightId(insight.id);
  };

  const isActive = insight.id === activeInsightId;

  return (
    <div
      className={clsx(styles.insightCardRoot, {
        [styles.isActive]: isActive,
      })}
      data-insight-id={dataInsightId}
      ref={ref}
      onClick={handleClick}
      data-testid="explore-insight-card"
    >
      <div className={styles.iconWrapper}>
        <InsightIconsRoot
          conditions={conditions}
          direction={exploreInsight.direction}
          customSize={isCombined ? COMBINE_ICONS_SIZE : SINGLE_ICONS_SIZE}
          isStarred={isNewInsight}
          isCombined={isCombined}
          layout="vertical"
        />
      </div>
      <div className={styles.infoWrapper}>
        <div className={styles.insightDetailsWrapper}>
          <InsightDetails
            name={simple_title || card.name}
            horizon={metadata.horizon}
            stars={metadata.stars}
            median={metadata.median_return}
            direction={formattedDirection}
            episodeLabel={episodeLabel}
            entityTag={entityTag}
          />
        </div>
        <div className={styles.analysisInfo}>
          <span>{isoDate}</span>
          {isNewInsight && (
            <Badge
              className={styles.badge}
              label={t('insights:insightCard.new')}
              data-testid="is-new-badge"
            />
          )}
        </div>
      </div>
    </div>
  );
};
